import { ArrowRight, Lock, Mail, Users } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useOrgUsersApi } from 'src/api/db/apiOrgUsers'
import { useOrgsApi } from 'src/api/db/apiOrgs'
import { useAcceptAdminInvite } from 'src/api/server/useAcceptAdminInvite'
import { useSupabaseClient } from 'src/api/supabase'
import { IMAGE_URLS } from 'src/assets/urlLinks'
import { LoginSignupPanel } from 'src/pages/LoginSignup/LoginSignupPanel'
import { paths } from 'src/routes/paths'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button, Card, Checkbox, Image } from 'src/shared/components/ui'
import { useAuth } from 'src/shared/hooks/authProvider'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { InvitePhoto } from './InviteValue'

const InviteShell = ({ children }: { children: ReactNode }) => (
  <Card
    variant="plain"
    wrapperClassName="w-full"
    className="w-full min-h-[min(100%,640px)] gap-0 overflow-hidden rounded-[1.5rem] p-0 shadow-[0_24px_80px_hsl(220_20%_12%/0.12)]"
  >
    <Row className="min-h-[min(100%,640px)] overflow-hidden">
      {children}
      <InvitePhoto />
    </Row>
  </Card>
)

export const InvitationContent = () => {
  const { t } = useLocalization()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isLoginMode, setIsLoginMode] = useState(false)
  const [isAccepting, setIsAccepting] = useState(false)
  const [bringCurrentOrders, setBringCurrentOrders] = useState(false)

  const emailFromUrl = searchParams.get('email') || ''
  const orgIdFromUrl = searchParams.get('organization_id') || ''
  const companyFromUrl = searchParams.get('company') || ''

  const acceptAdminInvite = useAcceptAdminInvite()
  const supabase = useSupabaseClient()
  const { session, loading: authLoading } = useAuth()
  const { organizationById } = useOrgsApi({ org_id: orgIdFromUrl })
  const { orgUser } = useOrgUsersApi()
  const { orgUsers } = useOrgUsersApi(orgIdFromUrl)

  const org = organizationById?.data
  const companyName = org?.name || companyFromUrl
  const logoUrl = org?.chosen_logo_url
  const hasEmail = !!session?.user?.email
  const memberCount = orgUsers?.data?.length ?? 0
  const initial = (companyName.trim()[0] || '?').toUpperCase()

  const invitePath = orgIdFromUrl
    ? `${paths.invitation}?${new URLSearchParams({
        organization_id: orgIdFromUrl,
        ...(emailFromUrl ? { email: emailFromUrl } : {}),
        ...(companyFromUrl ? { company: companyFromUrl } : {}),
      }).toString()}`
    : paths.invitation

  const nestedOrg = orgUser?.data?.organization
  const currentOrg = Array.isArray(nestedOrg) ? nestedOrg[0] : nestedOrg
  const hasExistingOrg = !!currentOrg?.name && currentOrg.name !== 'Anon'
  const oauthAutoAcceptStarted = useRef(false)

  const acceptAndEnter = async () => {
    if (!session?.user?.id || !orgIdFromUrl) return
    setIsAccepting(true)
    try {
      await acceptAdminInvite.mutateAsync({
        organization_id: orgIdFromUrl,
        user_id: session.user.id,
        email: emailFromUrl || session.user.email || undefined,
        bring_current_orders: bringCurrentOrders,
      })
      await supabase.auth.refreshSession()
      navigate(paths.myAccount)
    } finally {
      setIsAccepting(false)
    }
  }

  useEffect(() => {
    if (oauthAutoAcceptStarted.current) return
    if (authLoading || !hasEmail || !orgIdFromUrl) return
    if (searchParams.get('oauth') === null) return
    oauthAutoAcceptStarted.current = true
    void acceptAndEnter()
  }, [authLoading, hasEmail, orgIdFromUrl, searchParams])

  if (authLoading) {
    return (
      <InviteShell>
        <Column className="min-w-0 flex-1 items-center justify-center bg-white">
          <p className="text-muted-foreground">{t('Loading…', 'Laddar…')}</p>
        </Column>
      </InviteShell>
    )
  }

  if (!orgIdFromUrl) {
    return (
      <Card
        variant="plain"
        className="w-full gap-4 overflow-hidden rounded-[1.5rem] p-10 shadow-[0_24px_80px_hsl(220_20%_12%/0.12)]"
      >
        <h1>{t('Invite not found', 'Inbjudan hittades inte')}</h1>
        <p className="text-muted-foreground">
          {t(
            'This invite link is missing an organization. Ask your contact to send a new one.',
            'Den här inbjudningslänken saknar en organisation. Be er kontakt skicka en ny.'
          )}
        </p>
      </Card>
    )
  }

  return (
    <InviteShell>
      <Column className="min-w-0 flex-1 items-start justify-between overflow-y-auto bg-white px-12 py-10">
        <Column className="w-full max-w-md items-start gap-8">
          <Image
            src={IMAGE_URLS.logos.klayder}
            alt="Klayder"
            className="h-7 w-auto"
          />

          <Column className="gap-4">
            <div className="relative flex size-10 items-center justify-center rounded-full bg-violet-100">
              <Mail className="h-4 w-4 text-violet-600" />
              <span className="absolute right-0.5 top-0.5 size-2 rounded-full bg-violet-500" />
            </div>
            <Column className="gap-1.5">
              <h1>{t("You've been invited", 'Ni har bjudits in')}</h1>
              <p className="text-muted-foreground">
                {t(
                  "You've been invited to join an organization on Klayder.",
                  'Ni har bjudits in att gå med i en organisation på Klayder.'
                )}
              </p>
            </Column>
          </Column>

          <Row className="items-center gap-3 rounded-2xl border px-4 py-3">
            {logoUrl ? (
              <div className="size-10 shrink-0 overflow-hidden rounded-full bg-white">
                <Image
                  src={logoUrl}
                  alt=""
                  className="h-full w-full object-contain"
                />
              </div>
            ) : (
              <Column className="size-10 shrink-0 items-center justify-center rounded-full bg-[hsl(36_24%_90%)]">
                <span className="text-lg text-foreground/70">{initial}</span>
              </Column>
            )}
            <Column className="min-w-0 gap-0.5">
              <Row className="flex-wrap items-baseline gap-x-1.5">
                <span>
                  {companyName || t('An organization', 'En organisation')}
                </span>
                <span className="text-muted-foreground">
                  {t(
                    'invited you to join their organization',
                    'bjöd in er att gå med i deras organisation'
                  )}
                </span>
              </Row>
              {memberCount > 0 && (
                <Row className="items-center gap-1.5">
                  <Users className="h-3.5 w-3.5 text-violet-500" />
                  <span className="text-xs text-violet-600">
                    {memberCount === 1
                      ? t('1 member', '1 medlem')
                      : t(`${memberCount} members`, `${memberCount} medlemmar`)}
                  </span>
                </Row>
              )}
            </Column>
          </Row>

          {hasEmail ? (
            <Column className="gap-3">
              {hasExistingOrg && (
                <Checkbox
                  label={t(
                    'Bring current orders into the new organization',
                    'Ta med nuvarande ordrar till den nya organisationen'
                  )}
                  checked={bringCurrentOrders}
                  setChecked={setBringCurrentOrders}
                />
              )}
              <Button
                type="button"
                variant="primary"
                size="lg"
                className="w-full"
                disabled={isAccepting}
                isLoading={isAccepting}
                onClick={acceptAndEnter}
              >
                {t('Enter Klayder', 'Gå in i Klayder')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Column>
          ) : (
            <LoginSignupPanel
              loginModeSource="controlled"
              isLoginMode={isLoginMode}
              onLoginModeChange={setIsLoginMode}
              nextPath={invitePath}
              initialEmail={emailFromUrl}
              inviteOrganizationId={orgIdFromUrl}
              framed={false}
              formOnly
              className="w-full"
              onSignupOrgReady={() => acceptAndEnter()}
            />
          )}
        </Column>

        <Column className="w-full max-w-md items-center gap-1.5 pt-10">
          <Row className="items-center gap-1.5">
            <Lock className="h-3.5 w-3.5 text-muted-foreground" />
            <p className="text-muted-foreground">
              {t(
                'Secure. Private. Built for teams.',
                'Säkert. Privat. Byggt för team.'
              )}
            </p>
          </Row>
          <Link to={paths.about} className="text-muted-foreground">
            {t('Learn more about ', 'Läs mer om ')}
            <span className="underline">Klayder</span>
            {' >'}
          </Link>
        </Column>
      </Column>
    </InviteShell>
  )
}
